/**
 * How far through the notification feed this tab has already looked.
 *
 * `watchNotifications` keeps a high-water mark of its own -- `lastCount`, `seen` and `watermark` in
 * the library -- but all three live in PAGE memory and are re-seeded from scratch on every
 * navigation. That is why its first tick reads the list and deliberately emits nothing: it cannot
 * know what the previous page already saw. So it can never answer the one question that decides
 * whether this page has to pay for a reconcile -- "did anything arrive since the last time we
 * looked?"
 *
 * This module is that answer, in sessionStorage, with the same lifetime as the toast snapshot it
 * guards. Two fields, for the same two reasons the library's `absorb()` needs both -- a max
 * `receivedAt`, and a set of ids:
 *
 *   - rows can LEAVE the read window (it is the newest 100), and older rows can then enter it, which
 *     the id list alone would report as new. Hence the timestamp.
 *   - a row can carry NO timestamp (`receivedAt === ''`), which the timestamp alone would report as
 *     new forever. Hence the ids.
 *
 * Only the ids at the FRONTIER are kept -- the ones sharing the newest timestamp, plus any undated
 * row currently in the window. Everything older is already excluded by the timestamp, so storing it
 * would be dead weight, and this is what bounds the list. Storing "the ids of the last read" instead
 * is the obvious version and it is wrong: when a row leaves the read window the stored list shrinks,
 * the frontier row it was protecting stops being recognised, and the next read announces an award the
 * fan was already congratulated for.
 *
 * The mark is never CONSUMED -- reading it does not advance it, only `rememberWatermark()` does, and
 * only to what was actually read. A load that sees a stale list therefore catches the row on the next
 * navigation instead of losing it.
 *
 * Storage unavailable (private mode) degrades to "there are always arrivals", i.e. the unconditional
 * reconcile this layer shipped with. That is the safe direction, and it is the same direction
 * `reconcileLoyaltyToasts` degrades in -- it falls back to per-page memory in exactly these
 * conditions, so nothing was going to persist anyway.
 *
 * THE FAN IS IN THE KEY, SINCE 2026-09-07
 * ---------------------------------------
 * A mark is "how far through THIS FAN's feed we have looked", and it used to sit under one unscoped
 * key -- protected only by being cleared alongside the snapshot whenever an account switch was
 * detected. That works, and it is a guard, which means it has an ordering somebody can get wrong.
 * `toasts/enrolled.js` and `consent/store.js` both learned the same lesson and both put the fan
 * inside the entry, so a switch is a **miss by construction**. This is the third.
 *
 * **A fan this cannot identify gets no storage at all, and that is deliberate.** An empty `sub`
 * must never produce a shared key -- `urc.loyalty.toastFeedMark.` would recreate exactly the
 * unscoped-key bug of 2026-09-03. So it degrades to "there are always arrivals", the same direction
 * unavailable storage degrades in: one extra reconcile, never a swallowed award.
 *
 * **`clearWatermark( sub )` clears one fan's mark, not every fan's**, which means an A→B→A switch
 * inside one tab re-adopts A's mark rather than starting clean. That is correct and not a leak: the
 * mark is only ever read to decide whether to SKIP a reconcile, and a stale mark whose rows have
 * since moved on reports arrivals. What makes it safe even if it did not is `trigger.js`'s
 * `! hasToastSnapshot() || hasArrivalsSince( rows )` -- the snapshot is not fan-keyed and IS
 * cleared on an identity change, so the reconcile is forced regardless. Do not "simplify" that
 * `||`.
 */

/**
 * Where the mark lives, per fan.
 *
 * @param {string} fan The fan's Auth0 `sub`.
 * @return {string} The storage key.
 */
function markKey( fan ) {
	return `urc.loyalty.toastFeedMark.${ fan }`;
}

/** The unscoped key this module used until 2026-09-07. Cleared once, so it cannot linger. */
export const LEGACY_MARK_KEY = 'urc.loyalty.toastFeedMark';

/**
 * @param {string} key Storage key.
 * @return {string|null} The stored value, or null when absent or storage is unavailable.
 */
function read( key ) {
	try {
		return window.sessionStorage.getItem( key );
	} catch ( error ) {
		return null;
	}
}

/**
 * Whether this is a fan we can key storage on.
 *
 * @param {*} fan The candidate `sub`.
 * @return {boolean} True for a usable identity.
 */
function identified( fan ) {
	return typeof fan === 'string' && fan !== '';
}

/**
 * @param {string} key   Storage key.
 * @param {string} value What to store.
 * @return {void}
 */
function write( key, value ) {
	try {
		window.sessionStorage.setItem( key, value );
	} catch ( error ) {
		// Nothing to do: the next read answers null and the caller reconciles unconditionally.
	}
}

/**
 * The stored mark, or null when there is none we can trust.
 *
 * Anything unparseable or the wrong shape reads as absent rather than as an empty mark -- a mark
 * whose `ids` silently became `undefined` would suppress toasts, which is the one direction this
 * module must never fail in.
 *
 * @param {string} fan The fan's Auth0 `sub`.
 * @return {{at: string, ids: string[]}|null} The mark, or null.
 */
function readMark( fan ) {
	const raw = read( markKey( fan ) );

	if ( raw === null ) {
		return null;
	}

	try {
		const mark = JSON.parse( raw );

		if ( typeof mark?.at !== 'string' || ! Array.isArray( mark.ids ) ) {
			return null;
		}

		return mark;
	} catch ( error ) {
		return null;
	}
}

/**
 * Whether any of these rows arrived after the last remembered read.
 *
 * True when there is no mark at all: "we have never looked" is not "nothing happened", and the
 * caller's job in that case is to establish a baseline, which costs a reconcile.
 *
 * `>=` rather than `>`, matching the library: a row landing in the same second as one already
 * remembered is not silently dropped, and the id check is what stops that being a repeat.
 *
 * @param {Array<{id: string, receivedAt: string}>} rows One list read, as the library mapped it.
 * @param {string}                                  fan  The fan's Auth0 `sub`, or '' for none.
 * @return {boolean} Whether anything is new.
 */
export function hasArrivalsSince( rows, fan ) {
	// An unidentifiable fan has no mark and never gets one. "Always arrivals" is the safe answer:
	// one extra reconcile, never a swallowed award.
	if ( ! identified( fan ) ) {
		return true;
	}

	const mark = readMark( fan );

	if ( mark === null ) {
		return true;
	}

	const seen = new Set( mark.ids );

	return rows.some(
		row => ! seen.has( row.id ) && ( row.receivedAt === '' || row.receivedAt >= mark.at )
	);
}

/**
 * Record this read as the new position in the feed.
 *
 * `at` only ever grows: a read that came back short -- rows marked read, a narrower window, a stale
 * cache -- must not rewind the mark and re-announce what has already been shown.
 *
 * `ids` is rebuilt to the frontier of the NEW `at`, and merged with what was already stored only when
 * `at` did not move. When it did move, every previously stored id is by definition older than the new
 * frontier and the timestamp alone excludes it. When it did not, the stored ids are siblings at the
 * same instant and dropping them is exactly the shrinking bug described at the top of this file.
 *
 * @param {Array<{id: string, receivedAt: string}>} rows One list read, as the library mapped it.
 * @param {string}                                  fan  The fan's Auth0 `sub`, or '' for none.
 * @return {void}
 */
export function rememberWatermark( rows, fan ) {
	if ( ! identified( fan ) ) {
		return;
	}

	const previous = readMark( fan );
	const was = previous === null ? '' : previous.at;
	let at = was;

	for ( const row of rows ) {
		if ( row.receivedAt > at ) {
			at = row.receivedAt;
		}
	}

	// An undated row is carried indefinitely rather than by timestamp -- `hasArrivalsSince` has no
	// other way to tell "arrived while the clock was missing" from "already announced". Bounded by
	// the read window, which is the whole of what the route returns.
	const frontier = rows
		.filter( row => row.receivedAt === at || row.receivedAt === '' )
		.map( row => row.id );

	const ids =
		at === was && previous !== null
			? [ ...new Set( [ ...previous.ids, ...frontier ] ) ]
			: frontier;

	write( markKey( fan ), JSON.stringify( { at, ids } ) );
}

/**
 * Forget where we were, for one fan.
 *
 * Called wherever the toast snapshot is cleared. The two have to move together: a cleared snapshot
 * means the next reconcile can only re-baseline, so a mark left behind would let this tab skip that
 * reconcile and swallow the fan's next award.
 *
 * **The legacy unscoped key goes with every call**, so a tab carrying one from before 2026-09-07
 * cannot leave it behind for good. It is one `removeItem` on a key that is almost never there.
 *
 * @param {string} [fan] The fan's Auth0 `sub`. Omit to clear only the legacy key.
 * @return {void}
 */
export function clearWatermark( fan ) {
	try {
		if ( identified( fan ) ) {
			window.sessionStorage.removeItem( markKey( fan ) );
		}

		window.sessionStorage.removeItem( LEGACY_MARK_KEY );
	} catch ( error ) {
		// Storage unavailable, so there is nothing stored to remove.
	}
}
